import { lastValueFrom, map, startWith, switchMap, timer } from 'rxjs';
import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ResourceService } from '@core/services/resources.services';
import { AppService } from '@core/services/app.service';
import { FeatureAppActions } from './shared/store/app';
import { AuthService } from './core/services/auth.services';
import { Router, RouterModule } from '@angular/router';
import { SignalRService } from '@core/services/signalr.service';
import { ToastModule } from 'primeng/toast';
import { SpinnerComponent } from './pages/common-controls/spinner/spinner.component';
import { CapitalizePipe } from '@core/pipes/capitalize.pipe';
import { GlobalLoadingService } from '@core/services/globalLoading.service';
import { ClipboardService } from '@core/services/clipboard.service';
import { LogisticsServiceProviderBrandingService } from '@core/services/logistics-service-provider-branding.service';
import { ViewManagementService } from '@core/services/view-management.service';
import { SessionInitializationService } from '@core/services/session-initialization.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
  imports: [ToastModule, SpinnerComponent, RouterModule, CapitalizePipe]
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding('class.embedded-view') isEmbedded = false;
  private store = inject(Store);
  private resourceService = inject(ResourceService);
  private appService = inject(AppService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private signalRService = inject(SignalRService);
  private clipboardService = inject(ClipboardService);
  private brandingService = inject(LogisticsServiceProviderBrandingService);
  private loadingService = inject(GlobalLoadingService);
  private viewService = inject(ViewManagementService);
  private sessionInitService = inject(SessionInitializationService);

  public userAuthenticated = true;
  private delayedLoading$ = this.loadingService.loading$.pipe(
    switchMap((value) => (value?.active ? timer(1000).pipe(map(() => true)) : [false])),
    startWith(false)
  );
  public isActiveLoading = toSignal(this.delayedLoading$, { initialValue: false });

  constructor() {
    this.viewService.isEmbedded$.pipe(takeUntilDestroyed()).subscribe((isEmbedded) => {
      this.isEmbedded = isEmbedded;
    });
  }

  ngOnDestroy(): void {
    this.signalRService.stopConnection();
  }

  ngOnInit(): void {
    // The domain's branding, anonymously (US 3522). The signed-in user's own is layered over it by
    // SessionInitializationService; this is what the login screen and a cold load wear until then.
    void this.brandingService.applyForDomain();
    this.verifyUserAuthentication();
  }
  copyDetail(detail: string, event: MouseEvent): void {
    event.preventDefault();
    this.clipboardService.copyText(detail).then(() => {
      this.clipboardService.showCopyFeedbackNearElement(event.target);
    });
  }
  runToastAction(action: { run: () => void }, closeFn: (event: Event) => void, event: Event): void {
    event.preventDefault();
    action.run();
    closeFn(event);
  }
  reloadPage(event: Event) {
    event.preventDefault();
    window.location.reload();
  }
  verifyUserAuthentication() {
    if (!this.authService.isAuthenticated()) {
      this.userAuthenticated = false;
      return;
    }
    lastValueFrom(this.authService.userInfo())
      .then(async () => {
        // Load necessary resources
        lastValueFrom(this.resourceService.getResources('common'));
        // Initialize session with all required data and configurations.
        // The environment is fetched inside initializeSession() rather than here: this method
        // returns early when the token is not yet valid, which on the first load after a release
        // left the environment unset for the page's whole lifetime.
        await this.sessionInitService.initializeSession();
      })
      .catch(() => {
        this.sessionInitService
          .destroySession()
          .catch((error) => {
            console.error('Error destroying session after failed authentication check', error);
          })
          .finally(() => {
            this.router.navigate(['/login']);
          });
      })
      .finally(() => {
        this.userAuthenticated = false;
      });
  }
}
